import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FaUser,
  FaEnvelope,
  FaTrash,
  FaSpinner,
  FaCheck,
  FaLock,
  FaIdCard,
  FaShieldAlt,
} from 'react-icons/fa';
import { useAuth } from '../auth/context/AuthContext';
import ConfirmationDialog from '../restaurants/ConfirmationDialog';

const api = axios.create({
  baseURL: 'https://twiqli-server-vercel.vercel.app/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const OwnerSettings = () => {
  const [ownerData, setOwnerData] = useState({
    name: '',
    email: '',
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { ownerToken, ownerLogout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOwnerData = async () => {
      try {
        console.log(ownerToken);
        const response = await api.get('/users/profile', {
          headers: { Authorization: `Bearer ${ownerToken}` },
        });
        console.log(response);
        setOwnerData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching owner data:', error);
        toast.error('Failed to load your information. Please try again.');
        setLoading(false);
      }
    };

    fetchOwnerData();
  }, [ownerToken]);

  const handleInputChange = (e) => {
    setOwnerData({ ...ownerData, [e.target.name]: e.target.value });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const updateData = {
        name: ownerData.name,
        email: ownerData.email,
      };
      await api.put('/users/profile', updateData, {
        headers: { Authorization: `Bearer ${ownerToken}` },
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(
        error.response?.data?.message ||
          'Failed to update profile. Please try again.'
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    setUpdatingPassword(true);
    try {
      await api.put(
        '/users/update-password',
        {
          currentPassword,
          newPassword,
        },
        {
          headers: { Authorization: `Bearer ${ownerToken}` },
        }
      );
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error(
        error.response?.data?.message ||
          'Failed to update password. Please try again.'
      );
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.delete('/users/account', {
        headers: { Authorization: `Bearer ${ownerToken}` },
      });
      ownerLogout();
      toast.success('Your account has been deleted.');
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <FaSpinner className='animate-spin text-4xl text-blue-500' />
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8 max-w-3xl'>
      <h1 className='text-4xl font-bold mb-8 text-center text-gray-800'>
        Owner Account Settings
      </h1>
      <div className='bg-white shadow-2xl rounded-lg overflow-hidden'>
        <div className='p-8 space-y-8'>
          <div className='space-y-6'>
            <h2 className='text-2xl font-semibold text-gray-700 flex items-center'>
              <FaIdCard className='mr-3' /> Profile Information
            </h2>
            <form onSubmit={handleUpdateProfile} className='space-y-6'>
              <div>
                <label
                  htmlFor='name'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Name
                </label>
                <input
                  type='text'
                  id='name'
                  name='name'
                  value={ownerData.name}
                  onChange={handleInputChange}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Your name'
                />
              </div>
              <div>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Email
                </label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  value={ownerData.email}
                  onChange={handleInputChange}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Your email'
                />
              </div>
              <button
                type='submit'
                disabled={updating}
                className='w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200'
              >
                {updating ? (
                  <>
                    <FaSpinner className='animate-spin mr-2' />
                    Updating...
                  </>
                ) : (
                  <>
                    <FaCheck className='mr-2' />
                    Update Profile
                  </>
                )}
              </button>
            </form>
          </div>

          <div className='space-y-6'>
            <h2 className='text-2xl font-semibold text-gray-700 flex items-center'>
              <FaLock className='mr-3' /> Update Password
            </h2>
            <form onSubmit={handleUpdatePassword} className='space-y-6'>
              <div>
                <label
                  htmlFor='currentPassword'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Current Password
                </label>
                <input
                  type='password'
                  id='currentPassword'
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Enter current password'
                />
              </div>
              <div>
                <label
                  htmlFor='newPassword'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  New Password
                </label>
                <input
                  type='password'
                  id='newPassword'
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Enter new password'
                />
              </div>
              <div>
                <label
                  htmlFor='confirmPassword'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Confirm New Password
                </label>
                <input
                  type='password'
                  id='confirmPassword'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Confirm new password'
                />
              </div>
              <button
                type='submit'
                disabled={updatingPassword}
                className='w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-200'
              >
                {updatingPassword ? (
                  <>
                    <FaSpinner className='animate-spin mr-2' />
                    Updating Password...
                  </>
                ) : (
                  <>
                    <FaCheck className='mr-2' />
                    Update Password
                  </>
                )}
              </button>
            </form>
          </div>

          <div className='space-y-6'>
            <h2 className='text-2xl font-semibold text-red-600 flex items-center'>
              <FaTrash className='mr-3' /> Danger Zone
            </h2>
            <p className='text-gray-600'>
              Once you delete your account, there is no going back. Please be
              certain. This will also delete all your restaurants and associated
              data.
            </p>
            <button
              onClick={() => setShowDeleteConfirmation(true)}
              className='w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-200'
            >
              <FaTrash className='mr-2' />
              Delete Account
            </button>
          </div>
        </div>
      </div>
      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteAccount}
        title='Delete Account'
        message='Are you sure you want to delete your account? This action cannot be undone and will delete all your restaurants and associated data.'
      />
    </div>
  );
};

export default OwnerSettings;
